import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import {
  sortODataExportsByExportName,
  uniqODataExportsById,
} from '@/tools/viewODataExports/viewODataExports.utilities';
import { type ActionsFactory, createStore } from '@seeqdev/utils';
import type { BaseToolPersistedState } from '@/toolSelection/baseTool.store';
import type { ViewODataExportItem } from '@/tools/viewODataExports/viewODataExports.types';

/** View OData Exports store state. */
export interface ViewODataExportsState extends BaseToolPersistedState {
  /** The loaded OData export rows shown in the list. */
  items: ViewODataExportItem[];
  /** Whether the export list request is currently in flight. */
  loading: boolean;
  /** Whether another page of exports is available from the API. */
  hasNextPage: boolean;
}

export interface ViewODataExportsActions {
  setViewODataExports: (payload: { items: ViewODataExportItem[]; hasNextPage: boolean }) => void;
  setLoadingODataExports: (loading: boolean) => void;
  deleteODataExport: (id: string) => void;
  setViewODataExportElement: (item: ViewODataExportItem) => void;
  reset: (partialState?: Partial<ViewODataExportsState>) => void;
}

const initialState: ViewODataExportsState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  items: [],
  loading: false,
  hasNextPage: false,
});

const viewODataExportsActionsFactory: ActionsFactory<ViewODataExportsState, ViewODataExportsActions> = (
  set,
  _get,
  resetState,
) => ({
  setViewODataExports: (payload) =>
    set((draft) => {
      draft.items = uniqODataExportsById([...draft.items, ...payload.items]);
      draft.hasNextPage = payload.hasNextPage;
    }),

  setLoadingODataExports: (loading) =>
    set((draft) => {
      draft.loading = loading;
    }),

  deleteODataExport: (id) =>
    set((draft) => {
      draft.items = draft.items.filter((item) => item.id !== id);
    }),

  setViewODataExportElement: (item) =>
    set((draft) => {
      draft.items = sortODataExportsByExportName([...draft.items, { ...item }]);
    }),

  reset: (partialState?: Partial<ViewODataExportsState>) => resetState(partialState),
});

const [ViewODataExportsStoreProvider, useViewODataExportsStoreInstance, useViewODataExportsSelector] = createStore(
  'ViewODataExports',
  initialState,
  viewODataExportsActionsFactory,
);

export { ViewODataExportsStoreProvider };

/** The loaded OData export rows shown in the list. */
export const useViewODataExportsItems = () => useViewODataExportsSelector((state) => state.items);
/** Whether the export list request is currently in flight. */
export const useViewODataExportsIsLoading = () => useViewODataExportsSelector((state) => state.loading);
/** Whether another page of exports is available from the API. */
export const useViewODataExportsHasNextPage = () => useViewODataExportsSelector((state) => state.hasNextPage);
/** Actions for updating the View OData Exports store. */
export const useViewODataExportsActions = () => useViewODataExportsStoreInstance().actions;
